import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import FeatherIcon from 'feather-icons-react';

// Three-step demo of the extension's core loop: save a word on one page, meet it
// again highlighted on another page, then recall its meaning. Like the real
// extension, the lookup and the recall preview float over the page instead of
// pushing content down. They sit just below/above the word's line but are
// centered on the text block, so they never hang off a narrow phone screen.
// Recall is hover on devices that can hover, tap otherwise.

// Height of the fixed site header; the recall preview must not open underneath it.
const TOP_SAFE_AREA = 80;
const WORD = {
  text: 'negotiate',
  pronunciation: 'nɪˈɡoʊʃieɪt',
  translation: '交渉する',
  cefr_level: 'B2',
  part_of_speech: 'verb',
  senses: [
    { definition: 'to discuss something with someone in order to reach an agreement', example: 'We managed to negotiate a better price.' },
    { definition: 'to get past or through something difficult', example: 'She carefully negotiated the icy steps.' },
  ],
  forms: ['negotiates', 'negotiated', 'negotiating', 'negotiation'],
  often_appears_as: ['negotiate a deal', 'negotiate a salary'],
};

const pronounce = () => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  const uttr = new SpeechSynthesisUtterance(WORD.text);
  uttr.lang = 'en-US';
  uttr.rate = 0.85;
  window.speechSynthesis.speak(uttr);
};

const SectionLabel = ({ children }) => (
  <p style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em', color: '#a78bfa', textTransform: 'uppercase', margin: '10px 0 5px' }}>
    {children}
  </p>
);

const PAGES = {
  first: {
    url: 'worknews.example.com/article',
    before: 'Before the deadline, both teams met to ',
    after: ' a new delivery date for the project.',
  },
  second: {
    url: 'careerblog.example.com/freelancing',
    before: 'Before starting a new project, good freelancers ',
    after: ' fair rates with every client.',
  },
};

const STEPS = ['Save a word', 'Read another page', 'Recall it'];

const PURPLE = '#7c3aed';

const canHover = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: hover)').matches;

function StepIndicator({ current }) {
  return (
    <div className="d-flex justify-content-center flex-wrap" style={{ gap: '6px 14px', marginBottom: 16 }}>
      {STEPS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={label} className="d-flex align-items-center" style={{ gap: 6 }}>
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 700,
                background: done || active ? PURPLE : '#e2e8f0',
                color: done || active ? '#fff' : '#64748b',
              }}
            >
              {done ? <FeatherIcon icon="check" style={{ width: 12, height: 12 }} /> : i + 1}
            </span>
            <span style={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? '#0f172a' : '#64748b' }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function BrowserBar({ url }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderBottom: '1px solid #f1f3f5' }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#fca5a5' }} />
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#fde68a' }} />
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#86efac' }} />
      <span style={{ marginLeft: 10, fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url}</span>
    </div>
  );
}

// Mirrors the extension's lookup card: opens below the word's line, over the page.
function LookupPopover({ top, onSave, onClose }) {
  return (
    <div
      style={{
        position: 'absolute',
        top,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 20,
        width: 250,
        maxWidth: '100%',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 14,
        boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
        overflow: 'hidden',
        lineHeight: 'normal',
        textAlign: 'left',
        animation: 'hv-pop 0.18s ease-out',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 8px 6px 12px', borderBottom: '1px solid #f3f4f6' }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: PURPLE, textTransform: 'uppercase' }}>HighVocab</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', padding: 4 }}
        >
          <FeatherIcon icon="x" style={{ width: 14, height: 14 }} />
        </button>
      </div>
      <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{WORD.text}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: PURPLE, background: '#ede9fe', padding: '1px 6px', borderRadius: 4 }}>{WORD.cefr_level}</span>
        </div>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>/{WORD.pronunciation}/</span>
        <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.5, margin: 0 }}>{WORD.senses[0].definition}</p>
        <button
          type="button"
          onClick={onSave}
          style={{ width: '100%', padding: '8px 0', marginTop: 4, borderRadius: 8, border: 'none', background: PURPLE, color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
        >
          Save word
        </button>
      </div>
    </div>
  );
}

// Mirrors the extension's tooltip on already-saved, highlighted words: opens above the word's line,
// or below it when there isn't room above on screen (e.g. the card is near the top of a phone).
// Interactive (pronunciation button), so the parent keeps it open while the pointer is over it.
function HoverPreview({ anchor, onMouseEnter, onMouseLeave }) {
  const ref = useRef(null);
  const [below, setBelow] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !el.offsetParent) return;
    const wordTopOnScreen = el.offsetParent.getBoundingClientRect().top + anchor.top;
    setBelow(wordTopOnScreen - 10 - el.offsetHeight < TOP_SAFE_AREA);
  }, [anchor]);

  return (
    <div
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: below ? anchor.bottom + 8 : anchor.top - 10,
        left: '50%',
        transform: below ? 'translateX(-50%)' : 'translate(-50%, -100%)',
        zIndex: 20,
        width: 290,
        maxWidth: '100%',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 14,
        boxShadow: '0 12px 28px rgba(15,23,42,0.18)',
        padding: '12px 14px 14px',
        lineHeight: 'normal',
        textAlign: 'left',
        animation: 'hv-pop 0.18s ease-out',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>{WORD.text}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: PURPLE }}>{WORD.translation}</span>
        </div>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: PURPLE, background: '#ede9fe', padding: '1px 6px', borderRadius: 4 }}>{WORD.cefr_level}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
        <span style={{ fontSize: 11.5, color: '#9ca3af' }}>/{WORD.pronunciation}/</span>
        <button
          type="button"
          onClick={pronounce}
          aria-label="Play pronunciation"
          style={{ background: '#f5f3ff', border: 'none', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: PURPLE, cursor: 'pointer', padding: 0 }}
        >
          <FeatherIcon icon="volume-2" style={{ width: 12, height: 12 }} />
        </button>
        <span style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', marginLeft: 'auto' }}>{WORD.part_of_speech}</span>
      </div>

      <SectionLabel>Meanings</SectionLabel>
      {WORD.senses.map((sense, i) => (
        <div key={i} style={{ borderLeft: `2px solid ${i === 0 ? PURPLE : '#ddd6fe'}`, paddingLeft: 8, marginBottom: i === WORD.senses.length - 1 ? 0 : 8 }}>
          <p style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.45, margin: 0 }}>{sense.definition}</p>
          <p style={{ fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic', margin: '2px 0 0' }}>&ldquo;{sense.example}&rdquo;</p>
        </div>
      ))}

      <SectionLabel>Word forms</SectionLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {WORD.forms.map((f) => (
          <span key={f} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b' }}>{f}</span>
        ))}
      </div>

      <SectionLabel>Commonly used</SectionLabel>
      {WORD.often_appears_as.map((phrase) => (
        <p key={phrase} style={{ fontSize: 11.5, color: '#475569', margin: '0 0 2px' }}>• {phrase}</p>
      ))}
    </div>
  );
}

export default function ExtensionDemo() {
  const [hoverCapable] = useState(canHover);
  const [saved, setSaved] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [recalled, setRecalled] = useState(false);
  // Vertical position of the word within the text block, measured when a popover opens.
  const [anchor, setAnchor] = useState({ top: 0, bottom: 0 });
  const wordRef = useRef(null);

  const measureWord = () => {
    const el = wordRef.current;
    if (el) setAnchor({ top: el.offsetTop, bottom: el.offsetTop + el.offsetHeight });
  };

  // Text reflows on resize/rotation, so keep an open popover attached to the word.
  const popoverOpen = lookupOpen || previewOpen;
  useEffect(() => {
    if (!popoverOpen) return undefined;
    window.addEventListener('resize', measureWord);
    return () => window.removeEventListener('resize', measureWord);
  }, [popoverOpen]);

  const page = saved ? PAGES.second : PAGES.first;
  const stepIndex = recalled ? 2 : saved ? 1 : 0;

  // Short delay before closing so the pointer can travel from the word onto the preview.
  const closeTimer = useRef(null);
  const openHover = () => {
    clearTimeout(closeTimer.current);
    showPreview(true);
  };
  const closeHover = () => {
    closeTimer.current = setTimeout(() => setPreviewOpen(false), 150);
  };

  const showPreview = (open) => {
    if (open) measureWord();
    setPreviewOpen(open);
    if (open) setRecalled(true);
  };

  const handleWordClick = (e) => {
    e.stopPropagation();
    if (!saved) {
      measureWord();
      setLookupOpen((open) => !open);
    }
    else if (!hoverCapable) showPreview(!previewOpen);
  };

  const handleSave = () => {
    setLookupOpen(false);
    setSaved(true);
  };

  const reset = () => {
    setSaved(false);
    setLookupOpen(false);
    setPreviewOpen(false);
    setRecalled(false);
  };

  const wordStyle = saved
    ? {
        color: PURPLE,
        fontWeight: 700,
        borderBottom: `2px solid ${PURPLE}`,
        background: previewOpen ? '#ede9fe' : 'transparent',
        cursor: hoverCapable ? 'default' : 'pointer',
      }
    : {
        color: '#0f172a',
        fontWeight: 600,
        borderBottom: '2px solid transparent',
        background: lookupOpen ? '#c4b5fd' : '#fef08a',
        cursor: 'pointer',
      };

  const pulsing = (!saved && !lookupOpen) || (saved && !recalled);

  const hint = saved
    ? 'Later, on a different website'
    : `${hoverCapable ? 'Click' : 'Tap'} the highlighted word to try it`;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <style>{`
        @keyframes hv-pop {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes hv-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(124,58,237,0.35); }
          50% { box-shadow: 0 0 0 5px rgba(124,58,237,0); }
        }
      `}</style>

      <StepIndicator current={stepIndex} />

      <div
        onClick={() => {
          setLookupOpen(false);
          if (!hoverCapable) setPreviewOpen(false);
        }}
        style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: 16,
          boxShadow: '0 8px 28px rgba(15,23,42,0.06)',
          position: 'relative',
        }}
      >
        <BrowserBar url={page.url} />
        <div style={{ padding: '20px 20px 24px' }}>
          <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', color: '#a78bfa', textTransform: 'uppercase', margin: '0 0 10px', textAlign: 'center' }}>
            {hint}
          </p>

          <div style={{ position: 'relative' }}>
            <p key={page.url} style={{ fontSize: '1.08rem', lineHeight: 1.9, color: '#334155', margin: 0, minHeight: '3.8em', textAlign: 'center' }}>
              {page.before}
              <button
                ref={wordRef}
                type="button"
                onClick={handleWordClick}
                onMouseEnter={() => saved && hoverCapable && openHover()}
                onMouseLeave={() => saved && hoverCapable && closeHover()}
                style={{
                  border: 'none',
                  font: 'inherit',
                  lineHeight: 'inherit',
                  padding: '0 3px',
                  borderRadius: 4,
                  animation: pulsing ? 'hv-pulse 1.6s ease-in-out infinite' : 'none',
                  ...wordStyle,
                }}
              >
                {WORD.text}
              </button>
              {page.after}
            </p>
            {lookupOpen && <LookupPopover top={anchor.bottom + 8} onSave={handleSave} onClose={() => setLookupOpen(false)} />}
            {previewOpen && (
              <HoverPreview
                anchor={anchor}
                onMouseEnter={hoverCapable ? openHover : undefined}
                onMouseLeave={hoverCapable ? closeHover : undefined}
              />
            )}
          </div>

          {/* Fixed-height footer so the card doesn't change size between steps */}
          <div className="d-flex align-items-center justify-content-center flex-wrap text-center" style={{ gap: 8, marginTop: 12, minHeight: 24 }}>
            {saved && (
              <>
                <span style={{ fontSize: 12.5, color: '#64748b' }}>
                  <FeatherIcon icon="check-circle" style={{ width: 14, height: 14, color: PURPLE, verticalAlign: '-2px', marginRight: 4 }} />
                  Saved — {hoverCapable ? 'hover over' : 'tap'} the highlighted word to recall it.
                </span>
                {recalled && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); reset(); }}
                    className="d-inline-flex align-items-center"
                    style={{ gap: 4, background: 'none', border: 'none', padding: 0, color: PURPLE, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    <FeatherIcon icon="rotate-ccw" style={{ width: 12, height: 12 }} /> Try again
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
